import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Auto-surfaces active incidents from /api/status/active-incidents
 * inside DashboardShell. Dismissible per-session (localStorage of
 * dismissed incident ids); polls every 5 minutes.
 *
 * Blueprint Decision #45.
 */
const DISMISS_KEY = 'caraloom_dismissed_incidents_v1';

export const StatusBanner = () => {
  const [incidents, setIncidents] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'));
    } catch (e) { return new Set(); }
  });

  const persist = useCallback((set) => {
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(set)));
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/status/active-incidents`, { timeout: 8000 });
      setIncidents(data?.incidents || []);
    } catch (e) {
      // Silent — if the status endpoint is down, we don't spam users with a banner.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);   // 5 min poll
    return () => clearInterval(id);
  }, [load]);

  const dismiss = (incidentId) => {
    const next = new Set(dismissed);
    next.add(incidentId);
    setDismissed(next);
    persist(next);
  };

  const visible = incidents.filter((i) => !dismissed.has(i.id));
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map((i) => (
        <div
          key={i.id}
          className={`border-b px-4 py-2.5 ${
            i.severity === 'major'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
          role="alert"
          data-testid={`status-banner-${i.id}`}
        >
          <div className="cn-container flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <strong>{i.title}</strong>
              {i.impact && <span className="ml-2 opacity-80">{i.impact}</span>}
            </div>
            <Link
              to="/status"
              className="text-xs font-semibold underline whitespace-nowrap inline-flex items-center gap-1"
              data-testid={`status-banner-link-${i.id}`}
            >
              Status page <ExternalLink className="w-3 h-3" />
            </Link>
            <button
              type="button"
              onClick={() => dismiss(i.id)}
              aria-label="Dismiss"
              className="opacity-80 hover:opacity-100"
              data-testid={`status-banner-dismiss-${i.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </>
  );
};

export default StatusBanner;
